/**
 * 电机参数辨识状态 Store（Zustand）
 * 管理辨识进度、结果和电机参数
 */
import { create } from 'zustand';
import type { MotorIdentificationResult } from '@fluxlab/protocols';

type IdentState = 'idle' | 'running' | 'completed' | 'error';

interface IdentificationStore {
  state: IdentState;
  currentStep: number;
  totalSteps: number;
  stepMessage: string;
  result: MotorIdentificationResult | null;
  errorMessage: string | null;
  /** Saved motor params from MCU */
  motorParams: {
    polePairs: number;
    resistance: number;
    inductanceD: number;
    inductanceQ: number;
    fluxLinkage: number;
    inertia: number;
    friction: number;
    ratedCurrent: number;
    ratedSpeed: number;
    ratedTorque: number;
  } | null;

  setRunning: () => void;
  setProgress: (step: number, total: number, message: string) => void;
  setResult: (result: MotorIdentificationResult) => void;
  setMotorParams: (params: IdentificationStore['motorParams']) => void;
  setError: (message: string) => void;
  setIdle: () => void;
}

/** 电机参数辨识状态 Store（Zustand）：管理辨识进度、结果与电机参数 */
export const useIdentificationStore = create<IdentificationStore>((set) => ({
  state: 'idle',
  currentStep: 0,
  totalSteps: 0,
  stepMessage: '',
  result: null,
  errorMessage: null,
  motorParams: null,

  setRunning: () =>
    set((s) => ({
      state: 'running',
      currentStep: 0,
      totalSteps: 0,
      stepMessage: '',
      result: null,
      errorMessage: null,
      motorParams: s.motorParams, // preserve
    })),

  setProgress: (step, total, message) =>
    set({ currentStep: step, totalSteps: total, stepMessage: message }),

  setResult: (result) =>
    set({ state: 'completed', result }),

  setMotorParams: (params) =>
    set({ motorParams: params }),

  setError: (message) =>
    set({ state: 'error', errorMessage: message }),

  setIdle: () =>
    set({
      state: 'idle',
      currentStep: 0,
      totalSteps: 0,
      stepMessage: '',
      result: null,
      errorMessage: null,
    }),
}));
